import React, { useState, useMemo, useEffect, useRef } from 'react';
import type { Reservation, Table, Event, Order, FloorFixture } from '../types';
import ReservationModal from '../components/reservations/ReservationModal';
import EventModal from '../components/reservations/EventModal';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import RefreshIcon from '../components/icons/RefreshIcon';
import { emailService } from '../lib/email';
import {
  getReservationSlotLabel,
  getEventBookingDates,
  summarizeReservationGuestTotals,
  getNextEventIdForDefaultFilter,
  firstEventDateOnOrAfter,
} from '../lib/reservationCapacity';
import TablesReservationStatus from '../components/reservations/TablesReservationStatus';
import TableAssignmentModal from '../components/reservations/TableAssignmentModal';
import { formatIsoDateDdMmYyyy, todayLocalIsoDate } from '../lib/formatDisplayDate';

interface ReservationManagementPageProps {
  reservations: Reservation[];
  tables: Table[];
  events: Event[];
  orders: Order[];
  layoutComponents: FloorFixture[];
  /** Settings → Business info → Logo URL (shown in confirmation emails) */
  emailLogoUrl?: string;
  businessName?: string;
  onSave: (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdate: (id: number, updates: Partial<Reservation>) => void;
  onDelete: (id: number) => void;
  onSaveEvent: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateEvent: (id: string | number, updates: Partial<Event>) => void;
  onDeleteEvent: (id: string | number) => void;
  /** Refresh reservations, events, tables, orders (stays on this page) */
  onRefresh?: () => Promise<void>;
}

function SummaryStatCard({
  label,
  value,
  detail,
  hint,
  accentClass = 'text-gray-900',
}: {
  label: string;
  value: string | number;
  /** Extra line under the main value (e.g. remaining / breakdown) */
  detail?: string;
  hint?: string;
  accentClass?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${accentClass}`}>{value}</p>
      {detail ? (
        <p className="mt-1.5 text-sm font-semibold text-gray-800 tabular-nums leading-snug">{detail}</p>
      ) : null}
      {hint ? <p className="mt-1 text-xs text-gray-500 leading-snug">{hint}</p> : null}
    </div>
  );
}

const ReservationManagementPage: React.FC<ReservationManagementPageProps> = ({
  reservations,
  tables,
  events,
  orders,
  layoutComponents,
  emailLogoUrl,
  businessName,
  onSave,
  onUpdate,
  onDelete,
  onSaveEvent,
  onUpdateEvent,
  onDeleteEvent,
  onRefresh,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => todayLocalIsoDate());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterEventId, setFilterEventId] = useState<string>('all');
  const [filterSlotId, setFilterSlotId] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [assignmentReservation, setAssignmentReservation] = useState<Reservation | null>(null);
  const defaultEventFilterAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultEventFilterAppliedRef.current || events.length === 0) return;
    const today = todayLocalIsoDate();
    const nextId = getNextEventIdForDefaultFilter(events, today);
    if (nextId !== 'all') {
      setFilterEventId(nextId);
      const ev = events.find((e) => String(e.id) === nextId);
      const datePick = ev ? firstEventDateOnOrAfter(ev, today) : undefined;
      if (datePick) setSelectedDate(datePick);
    }
    defaultEventFilterAppliedRef.current = true;
  }, [events]);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredReservations = reservations.filter((res) => {
    // Events page: general (no eventId) bookings are managed on POS only
    if (res.eventId == null || res.eventId === '') return false;
    const matchesDate = res.reservationDate === selectedDate;
    const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
    const matchesEvent =
      filterEventId === 'all' ||
      String(res.eventId) === filterEventId;
    const matchesSlot =
      filterSlotId === 'all' ||
      (res.slotId != null && res.slotId === filterSlotId);
    return matchesDate && matchesStatus && matchesEvent && matchesSlot;
  });

  const guestTotals = summarizeReservationGuestTotals(filteredReservations);

  const venueSeatCapacity = useMemo(
    () => tables.reduce((sum, t) => sum + (Number(t.capacity) || 0), 0),
    [tables]
  );

  const capacityFigure = useMemo(() => {
    const ev =
      filterEventId !== 'all' ? events.find((e) => String(e.id) === filterEventId) : undefined;
    if (ev?.maxCapacity != null && ev.maxCapacity >= 0) {
      return { value: ev.maxCapacity, hint: `Cap for “${ev.name}”` };
    }
    return {
      value: venueSeatCapacity,
      hint: 'Sum of table capacities',
    };
  }, [filterEventId, events, venueSeatCapacity]);

  /** Confirmed party sizes only, for selected date + event scope (ignores status filter). */
  const confirmedPartySizeForCapacity = useMemo(() => {
    return reservations
      .filter((res) => {
        if (res.reservationDate !== selectedDate) return false;
        if (res.status !== 'confirmed') return false;
        if (filterEventId !== 'all') {
          return res.eventId != null && String(res.eventId) === filterEventId;
        }
        return true;
      })
      .reduce((sum, r) => sum + (Number(r.partySize) || 0), 0);
  }, [reservations, selectedDate, filterEventId]);

  const capacityRemaining = Math.max(0, capacityFigure.value - confirmedPartySizeForCapacity);

  const selectedEvent =
    filterEventId !== 'all'
      ? events.find((e) => !e.deleted && String(e.id) === filterEventId)
      : undefined;

  const filterSlots = selectedEvent?.mealSlots ?? [];

  const eventsForDate = events.filter((e) => {
    if (e.deleted) return false;
    const dates = getEventBookingDates(e);
    return dates.includes(selectedDate) || dates.some((d) => d >= selectedDate);
  });

  const getEventForReservation = (res: Reservation) =>
    res.eventId != null ? events.find((ev) => String(ev.id) === String(res.eventId)) : undefined;

  const handleOpenModal = (reservation: Reservation | null = null) => {
    setEditingReservation(reservation);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReservation(null);
  };

  const handleOpenEventModal = (event: Event | null = null) => {
    setEditingEvent(event);
    setIsEventModalOpen(true);
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    setEditingEvent(null);
  };

  const handleSave = async (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingReservation) {
        await onUpdate(editingReservation.id, reservation);
      } else {
        await onSave(reservation);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save reservation:', error);
      alert('Failed to save reservation. Please try again.');
    }
  };

  const handleSaveEvent = async (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingEvent) {
        await onUpdateEvent(editingEvent.id, event);
      } else {
        await onSaveEvent(event);
      }
      handleCloseEventModal();
    } catch (error) {
      console.error('Failed to save event:', error);
      alert('Failed to save event. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this reservation?')) {
      try {
        await onDelete(id);
      } catch (error) {
        console.error('Failed to delete reservation:', error);
        alert('Failed to delete reservation. Please try again.');
      }
    }
  };

  const handleStatusChange = async (id: number, newStatus: Reservation['status']) => {
    try {
      await onUpdate(id, { status: newStatus });
    } catch (error) {
      console.error('Failed to update reservation status:', error);
      alert('Failed to update reservation status. Please try again.');
    }
  };

  const toggleArrival = async (reservation: Reservation, arrived: boolean) => {
    const current = reservation.status;
    const next: Reservation['status'] =
      arrived && current !== 'seated'
        ? 'seated'
        : !arrived && current === 'seated'
          ? 'confirmed'
          : current;
    if (next === current) return;
    await handleStatusChange(reservation.id, next);
  };

  const buildTableInfo = (res: Reservation) =>
    res.assignedTableIds?.length && res.assignedTableIds.length > 0
      ? res.assignedTableIds
          .map((tid) => tables.find((t) => t.id === tid)?.name ?? `Table ${tid}`)
          .join(', ')
      : res.tableId
        ? (tables.find((t) => t.id === res.tableId)?.name ?? `Table ${res.tableId}`)
        : undefined;

  const buildReservationConfirmationParams = (reservation: Reservation) => {
    const ev = getEventForReservation(reservation);
    const tableInfo = buildTableInfo(reservation);
    const showTable = ev?.showTableInCustomerComms !== false;
    const slotLabel = getReservationSlotLabel(reservation, ev);
    const allergySummary =
      reservation.foodAllergyDeclaration === 'has_allergies'
        ? reservation.allergyDetails?.trim() || 'Yes (see special requests)'
        : reservation.foodAllergyDeclaration === 'none'
          ? 'No known allergies declared'
          : undefined;
    return {
      customerName: reservation.customerName,
      reservationDate: reservation.reservationDate,
      reservationTime: reservation.reservationTime,
      partySize: reservation.partySize,
      ...(reservation.adultsCount != null && { adultsCount: reservation.adultsCount }),
      ...(reservation.kidsCount != null && { kidsCount: reservation.kidsCount }),
      ...(reservation.kidsBelow10Count != null && {
        kidsBelow10Count: reservation.kidsBelow10Count,
      }),
      ...(reservation.kidsAbove10Count != null && {
        kidsAbove10Count: reservation.kidsAbove10Count,
      }),
      tableInfo: showTable ? tableInfo : undefined,
      omitTableFromCustomerComms: !showTable,
      eventName: ev?.name,
      parkingInstructions: ev?.parkingInstructions?.trim() || undefined,
      bookingHeroImageUrl: ev?.bookingHeroImageUrl?.trim() || undefined,
      logoUrl: emailLogoUrl?.trim() || undefined,
      businessName: businessName?.trim() || undefined,
      slotLabel,
      allergySummary,
    };
  };

  const handleConfirmAndSendEmail = async (reservation: Reservation) => {
    if (!reservation.customerEmail?.trim()) {
      alert('No email on file. Add the customer email and try again.');
      return;
    }
    try {
      await onUpdate(reservation.id, { status: 'confirmed' });
      const result = await emailService.sendReservationConfirmation(
        reservation.customerEmail.trim(),
        buildReservationConfirmationParams(reservation)
      );
      if (result.success) {
        await onUpdate(reservation.id, { confirmationEmailSentAt: Date.now() });
        alert('Reservation confirmed and confirmation email sent.');
      } else {
        alert('Reservation updated but email failed: ' + (result.error ?? 'Unknown error'));
      }
    } catch (error) {
      console.error('Confirm & send email failed:', error);
      alert('Failed to confirm or send email. Please try again.');
    }
  };

  const handleSendConfirmationEmail = async (reservation: Reservation) => {
    if (!reservation.customerEmail?.trim()) {
      alert('No email on file. Add the customer email and try again.');
      return;
    }
    try {
      const result = await emailService.sendReservationConfirmation(
        reservation.customerEmail.trim(),
        buildReservationConfirmationParams(reservation)
      );
      if (result.success) {
        await onUpdate(reservation.id, { confirmationEmailSentAt: Date.now() });
        alert('Confirmation email sent.');
      } else {
        alert('Email failed: ' + (result.error ?? 'Unknown error'));
      }
    } catch (error) {
      console.error('Send confirmation email failed:', error);
      alert('Failed to send email. Please try again.');
    }
  };

  const getStatusColor = (status: Reservation['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'waitlist':
        return 'bg-amber-100 text-amber-800';
      case 'seated':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTableName = (tableId: number) => {
    const table = tables.find((t) => t.id === tableId);
    return table ? table.name : `Table ${tableId}`;
  };

  const getTableDisplay = (res: Reservation) => {
    if (res.assignedTableIds?.length) {
      return res.assignedTableIds
        .map((tid) => getTableName(tid))
        .join(', ');
    }
    if (res.tableId) return getTableName(res.tableId);
    return res.requiresMerge ? 'Merge on arrival' : 'To assign';
  };

  const handleTableAssignmentConfirm = async (updates: {
    tableId: number;
    assignedTableIds?: number[];
    requiresMerge?: boolean;
  }) => {
    if (!assignmentReservation) return;
    try {
      await onUpdate(assignmentReservation.id, updates);
      setAssignmentReservation(null);
    } catch (err) {
      console.error('Table assignment failed:', err);
      alert('Failed to save table assignment. Please try again.');
    }
  };

  const getEventName = (eventId: string | number | undefined) => {
    if (eventId == null) return '—';
    const e = events.find((ev) => !ev.deleted && String(ev.id) === String(eventId));
    return e ? e.name : '—';
  };

  const getBookingLink = (eventId: string | number) => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    return `${base}/book?event=${encodeURIComponent(String(eventId))}`;
  };

  const copyBookingLink = (eventId: string | number) => {
    const url = getBookingLink(eventId);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => alert('Booking link copied to clipboard. Share it with customers.'),
        () => fallbackCopy(url)
      );
    } else {
      fallbackCopy(url);
    }
  };

  const fallbackCopy = (url: string) => {
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      alert('Booking link copied. Share it with customers.');
    } catch {
      alert('Link: ' + url);
    }
    document.body.removeChild(ta);
  };

  const handleExportCsv = () => {
    if (filteredReservations.length === 0) {
      alert('No reservations to export for the current filters.');
      return;
    }
    const headers = [
      'Date',
      'Time',
      'Event',
      'Customer',
      'Phone',
      'Email',
      'PartySize',
      'Adults',
      'KidsTotal',
      'Status',
      'Tables',
      'Notes',
    ];
    const rows = filteredReservations.map((r) => {
      const evName = getEventName(r.eventId);
      const table = getTableDisplay(r) ?? '';
      const kidsTotal =
        (r.kidsBelow10Count ?? 0) +
        (r.kidsAbove10Count ?? 0) +
        (r.kidsCount && !r.kidsBelow10Count && !r.kidsAbove10Count ? r.kidsCount : 0);
      const cols = [
        r.reservationDate,
        r.reservationTime,
        evName,
        r.customerName,
        r.customerPhone,
        r.customerEmail ?? '',
        String(r.partySize),
        r.adultsCount != null ? String(r.adultsCount) : '',
        kidsTotal ? String(kidsTotal) : '',
        r.status,
        table,
        r.notes ?? '',
      ];
      return cols
        .map((c) => {
          const v = c ?? '';
          if (v.includes('"') || v.includes(',') || v.includes('\n')) {
            return `"${v.replace(/"/g, '""')}"`;
          }
          return v;
        })
        .join(',');
    });
    const csv = [headers.join(','), ...rows].join('\r\n');
    try {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservations-${selectedDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
      alert('Could not export CSV in this browser.');
    }
  };

  const handlePrintList = () => {
    if (filteredReservations.length === 0) {
      alert('No reservations to print for the current filters.');
      return;
    }
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      alert('Popup blocked. Please allow popups to print.');
      return;
    }
    const title = `Reservations – ${selectedDate}${
      filterEventId !== 'all' ? ` (${getEventName(filterEventId as any)})` : ''
    }`;
    const rowsHtml = filteredReservations
      .map((r) => {
        const evName = getEventName(r.eventId);
        const table = getTableDisplay(r) ?? '';
        const kidsSummary =
          r.kidsBelow10Count != null || r.kidsAbove10Count != null
            ? `A${r.adultsCount ?? '—'} · <10:${r.kidsBelow10Count ?? 0} · 10+:${
                r.kidsAbove10Count ?? 0
              }`
            : '';
        return `<tr>
  <td>${r.reservationTime}</td>
  <td>${r.customerName}</td>
  <td>${r.partySize}</td>
  <td>${kidsSummary}</td>
  <td>${evName}</td>
  <td>${table}</td>
  <td>${r.status}</td>
</tr>`;
      })
      .join('\n');
    w.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111827; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      h2 { font-size: 14px; margin-top: 0; color: #4b5563; }
      table { border-collapse: collapse; width: 100%; margin-top: 16px; font-size: 12px; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
      th { background: #f3f4f6; }
    </style>
  </head>
  <body>
    <h1>${title}</h1>
    <h2>${guestTotals.bookingCount} bookings · ${guestTotals.partySize} guests</h2>
    <table>
      <thead>
        <tr>
          <th>Time</th>
          <th>Customer</th>
          <th>Party</th>
          <th>Adults / Kids</th>
          <th>Event</th>
          <th>Table(s)</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </body>
</html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleSoftDeleteEvent = async (event: Event) => {
    if (
      !window.confirm(
        `Delete event “${event.name}”? This hides it from booking links and lists but keeps existing reservations.`
      )
    ) {
      return;
    }
    try {
      await onUpdateEvent(event.id, { deleted: true });
    } catch (err) {
      console.error('Soft delete event failed', err);
      alert('Could not delete event. Please try again.');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Events</h1>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  title="Refresh data (stay on this page)"
                  className="bg-gray-100 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-300 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshIcon className={`w-5 h-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleOpenEventModal()}
                className="bg-gray-100 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-300 flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                New Event
              </button>
              <button
                onClick={() => handleOpenModal()}
                className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                New Reservation
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Filters</h2>
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="waitlist">Waitlist</option>
                  <option value="seated">Seated</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="min-w-[200px] flex-1 max-w-md">
                <label className="block text-xs font-medium text-gray-600 mb-1">Event</label>
                <select
                  value={filterEventId}
                  onChange={(e) => setFilterEventId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="all">All events</option>
                  {events.filter((ev) => !ev.deleted).map((ev) => (
                    <option key={String(ev.id)} value={String(ev.id)}>
                      {ev.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedEvent && filterSlots.length > 0 && (
                <div className="min-w-[200px] flex-1 max-w-md">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Slot / sitting
                  </label>
                  <select
                    value={filterSlotId}
                    onChange={(e) => setFilterSlotId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="all">All slots</option>
                    {filterSlots.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                        {s.startTime ? ` (${s.startTime}${s.endTime ? `–${s.endTime}` : ''})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Bookings, adults, and kids match Date, Status, Event, and Slot. Capacity remaining
              uses{' '}
              <span className="font-medium text-gray-600">confirmed</span> guests for the selected
              date and event only.
            </p>
          </section>

          <section aria-label="Summary statistics">
            <h2 className="sr-only">Summary statistics</h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              <SummaryStatCard
                label="Total capacity"
                value={capacityFigure.value}
                detail={`${capacityRemaining} left · ${confirmedPartySizeForCapacity} confirmed`}
                hint={`${capacityFigure.hint}. Confirmed = party size sum.`}
                accentClass="text-pink-700"
              />
              <SummaryStatCard
                label="Bookings"
                value={guestTotals.bookingCount}
                hint={
                  guestTotals.bookingCount > 0
                    ? `${guestTotals.partySize} guests (party size)`
                    : 'No rows match filters'
                }
              />
              <SummaryStatCard
                label="Adults"
                value={guestTotals.adults}
                hint="From guest breakdown where provided"
                accentClass="text-slate-800"
              />
              <SummaryStatCard
                label="Kids"
                value={guestTotals.kids}
                hint="Under 10 + 10+, or child count"
                accentClass="text-slate-800"
              />
            </div>
          </section>

          {eventsForDate.length > 0 && (
            <section className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Events on this date</h3>
              <ul className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
                {eventsForDate.slice(0, 12).map((ev) => {
                  const eventDatesLabel = getEventBookingDates(ev)
                    .map((d) => formatIsoDateDdMmYyyy(d))
                    .join(' · ');
                  return (
                  <li
                    key={String(ev.id)}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200/80 bg-white px-3 py-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">
                        {ev.name}{' '}
                        <span className="font-normal text-gray-500">
                          ({ev.startTime}–{ev.endTime})
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">{eventDatesLabel}</div>
                    </div>
                    <span className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => copyBookingLink(ev.id)}
                        className="text-green-700 hover:text-green-900 text-xs font-semibold"
                        title="Copy customer booking link"
                      >
                        Copy link
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenEventModal(ev)}
                        className="text-pink-600 hover:text-pink-800 text-xs font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSoftDeleteEvent(ev)}
                        className="text-red-600 hover:text-red-800 text-xs font-semibold"
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                );
                })}
              </ul>
            </section>
          )}

          <section aria-label="Reservation list">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Reservation list</h3>
                <span className="text-xs text-gray-500 tabular-nums">
                  {guestTotals.bookingCount} shown
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handlePrintList}
                  className="hidden sm:inline-flex items-center rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Print list
                </button>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Export CSV
                </button>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {filteredReservations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No reservations found for the selected date and filters.</p>
            </div>
          ) : (
            <>
              {/* Tablet/mobile: card layout so details + actions are visible without horizontal scroll */}
              <div className="lg:hidden space-y-3 p-4">
                {filteredReservations.map((reservation) => {
                  const resEv = getEventForReservation(reservation);
                  const slotLb = getReservationSlotLabel(reservation, resEv);
                  return (
                  <div
                    key={reservation.id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 hover:bg-gray-50"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div>
                        <span className="font-semibold text-gray-900 mr-2">{reservation.reservationTime}</span>
                        {slotLb && (
                          <span className="block text-xs text-pink-700 font-medium">{slotLb}</span>
                        )}
                        <span className="font-medium text-gray-900">{reservation.customerName}</span>
                        <span className="text-gray-600 ml-1">({reservation.partySize})</span>
                        {(reservation.kidsBelow10Count != null ||
                          reservation.kidsAbove10Count != null) && (
                          <span className="block text-xs text-gray-500">
                            A{reservation.adultsCount ?? '—'} · &lt;10:{' '}
                            {reservation.kidsBelow10Count ?? 0} · 10+:{' '}
                            {reservation.kidsAbove10Count ?? 0}
                          </span>
                        )}
                        {reservation.foodAllergyDeclaration === 'has_allergies' && (
                          <span className="block text-xs text-amber-800 font-medium">Allergies on file</span>
                        )}
                      </div>
                      <select
                        value={reservation.status}
                        onChange={(e) =>
                          handleStatusChange(reservation.id, e.target.value as Reservation['status'])
                        }
                        className={`px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(reservation.status)} border-0 focus:outline-none focus:ring-2 focus:ring-pink-500`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="waitlist">Waitlist</option>
                        <option value="seated">Seated</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div className="text-sm text-gray-600 space-y-0.5 mb-3">
                      {reservation.customerEmail && (
                        <div className="truncate" title={reservation.customerEmail}>{reservation.customerEmail}</div>
                      )}
                      {reservation.customerPhone && (
                        <div>{reservation.customerPhone}</div>
                      )}
                      <div>
                        {getEventName(reservation.eventId)} ·{' '}
                        <button
                          type="button"
                          onClick={() => setAssignmentReservation(reservation)}
                          className="text-pink-600 hover:text-pink-800 font-medium underline"
                        >
                          {getTableDisplay(reservation)}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-top border-gray-200">
                      <label className="inline-flex items-center gap-1 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                          checked={reservation.status === 'seated'}
                          onChange={(e) => toggleArrival(reservation, e.target.checked)}
                        />
                        <span>Arrived</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setAssignmentReservation(reservation)}
                        className="text-sm text-pink-600 hover:text-pink-800 font-medium"
                      >
                        Assign tables
                      </button>
                      {(reservation.status === 'waitlist' || reservation.status === 'pending') &&
                        reservation.customerEmail && (
                          <button
                            type="button"
                            onClick={() => handleConfirmAndSendEmail(reservation)}
                            className="text-green-600 hover:text-green-800 font-medium text-sm"
                          >
                            Confirm & email
                          </button>
                        )}
                      {(reservation.status === 'confirmed' || reservation.status === 'seated') &&
                        reservation.customerEmail && (
                        <>
                          {reservation.confirmationEmailSentAt && (
                            <span className="text-xs text-gray-500">
                              Sent {new Date(reservation.confirmationEmailSentAt).toLocaleString()}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleSendConfirmationEmail(reservation)}
                            className="text-green-600 hover:text-green-800 font-medium text-sm"
                          >
                            {reservation.confirmationEmailSentAt ? 'Resend email' : 'Send email'}
                          </button>
                        </>
                      )}
                      <div className="flex items-center gap-1 ml-auto">
                        <button
                          onClick={() => handleOpenModal(reservation)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <EditIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(reservation.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>

              {/* Desktop: table layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table(s)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Party / guests
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReservations.map((reservation) => {
                      const resEv = getEventForReservation(reservation);
                      const slotLb = getReservationSlotLabel(reservation, resEv);
                      return (
                      <tr key={reservation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {reservation.reservationTime}
                          {slotLb && (
                            <div className="text-xs text-pink-700 font-normal">{slotLb}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservation.customerName}
                          {reservation.customerEmail && (
                            <div className="text-xs text-gray-500">{reservation.customerEmail}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getEventName(reservation.eventId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            type="button"
                            onClick={() => setAssignmentReservation(reservation)}
                            className="text-pink-600 hover:text-pink-800 font-medium text-left hover:underline"
                            title="Assign or change tables"
                          >
                            {getTableDisplay(reservation)}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservation.partySize}
                          {(reservation.kidsBelow10Count != null ||
                            reservation.kidsAbove10Count != null) && (
                            <div className="text-xs text-gray-500 font-normal">
                              A{reservation.adultsCount ?? '—'} · &lt;10:{' '}
                              {reservation.kidsBelow10Count ?? 0} · 10+:{' '}
                              {reservation.kidsAbove10Count ?? 0}
                            </div>
                          )}
                          {reservation.foodAllergyDeclaration === 'has_allergies' && (
                            <div className="text-xs text-amber-800">Allergies</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservation.customerPhone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                                checked={reservation.status === 'seated'}
                                onChange={(e) => toggleArrival(reservation, e.target.checked)}
                              />
                              <span>Arrived</span>
                            </label>
                            <select
                              value={reservation.status}
                              onChange={(e) =>
                                handleStatusChange(
                                  reservation.id,
                                  e.target.value as Reservation['status']
                                )
                              }
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                reservation.status
                              )} border-0 focus:outline-none focus:ring-2 focus:ring-pink-500`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="waitlist">Waitlist</option>
                              <option value="seated">Seated</option>
                              <option value="cancelled">Cancelled</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col gap-1">
                            {(reservation.status === 'waitlist' || reservation.status === 'pending') &&
                              reservation.customerEmail && (
                                <button
                                  type="button"
                                  onClick={() => handleConfirmAndSendEmail(reservation)}
                                  className="text-green-600 hover:text-green-900 whitespace-nowrap text-left"
                                  title="Confirm and send email"
                                >
                                  Confirm & email
                                </button>
                              )}
                            {(reservation.status === 'confirmed' || reservation.status === 'seated') &&
                              reservation.customerEmail && (
                              <div className="flex flex-col gap-0.5">
                                {reservation.confirmationEmailSentAt ? (
                                  <span className="text-xs text-gray-500" title="Last sent">
                                    Sent {new Date(reservation.confirmationEmailSentAt).toLocaleString()}
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => handleSendConfirmationEmail(reservation)}
                                  className="text-green-600 hover:text-green-900 whitespace-nowrap text-left"
                                  title={reservation.confirmationEmailSentAt ? 'Send confirmation email again' : 'Send confirmation email'}
                                >
                                  {reservation.confirmationEmailSentAt ? 'Resend email' : 'Send email'}
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleOpenModal(reservation)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit"
                              >
                                <EditIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(reservation.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
            </div>
          </section>

          <section aria-label="Floor plan booking status">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Tables & floor</h3>
            <TablesReservationStatus
              tables={tables}
              reservations={reservations}
              selectedDate={selectedDate}
              layoutComponents={layoutComponents}
            />
          </section>
        </div>
      </main>

      {assignmentReservation && (
        <TableAssignmentModal
          reservation={assignmentReservation}
          tables={tables}
          reservations={reservations}
          onConfirm={handleTableAssignmentConfirm}
          onClose={() => setAssignmentReservation(null)}
        />
      )}

      {isModalOpen && (
        <ReservationModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          reservation={editingReservation}
          tables={tables}
          reservations={reservations}
          events={events}
        />
      )}

      {isEventModalOpen && (
        <EventModal
          isOpen={isEventModalOpen}
          onClose={handleCloseEventModal}
          onSave={handleSaveEvent}
          event={editingEvent}
        />
      )}
    </div>
  );
};

export default ReservationManagementPage;
